// @core/stores/authz/AuthzStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { authzApi } from '@core/api';
import type {
  BatchCheckRequest,
  BatchCheckResponse,
  CheckRequest,
  CheckResponse,
  ExpandRequest,
  ExpandResponse,
  ListAuthorizationModelsResponse,
  ListObjectsRequest,
  ListObjectsResponse,
  ListUsersRequest,
  ListUsersResponse,
  ReadAuthorizationModelResponse,
  ReadTuplesRequest,
  ReadTuplesResponse,
  TupleChangeEvent,
  WriteAuthorizationModelRequest,
  WriteAuthorizationModelResponse,
  WriteTuplesResponse,
} from '@core/types/authz';
import type { PaginatedRequest } from '@core/types/pagination';

class AuthzStore {

  // Loading states
  isLoading = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;

  constructor() {
    makeAutoObservable(this);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }

  reset(): void {
    this.error = null;
  }

  /**
   * Batch check authorization
   */
  async BatchCheck(data: BatchCheckRequest): Promise<BatchCheckResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authzApi.BatchCheck(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Check authorization
   */
  async Check(data: CheckRequest): Promise<CheckResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authzApi.Check(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Delete authorization tuples (internal API)
   */
  async DeleteTuples(data: TupleChangeEvent): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authzApi.DeleteTuples(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Expand permission tree
   */
  async Expand(data: ExpandRequest): Promise<ExpandResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authzApi.Expand(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List authorization models
   */
  async ListAuthorizationModels(params?: PaginatedRequest): Promise<ListAuthorizationModelsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authzApi.ListAuthorizationModels(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List accessible objects
   */
  async ListObjects(data: ListObjectsRequest): Promise<ListObjectsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authzApi.ListObjects(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List users with access
   */
  async ListUsersByObject(data: ListUsersRequest): Promise<ListUsersResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authzApi.ListUsersByObject(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get authorization model
   */
  async ReadAuthorizationModel(id: string): Promise<ReadAuthorizationModelResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authzApi.ReadAuthorizationModel(id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Read authorization tuples
   */
  async ReadTuples(data: ReadTuplesRequest): Promise<ReadTuplesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authzApi.ReadTuples(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create authorization model
   */
  async WriteAuthorizationModel(data: WriteAuthorizationModelRequest): Promise<WriteAuthorizationModelResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authzApi.WriteAuthorizationModel(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Write authorization tuples (internal API)
   */
  async WriteTuples(data: TupleChangeEvent): Promise<WriteTuplesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await authzApi.WriteTuples(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const authzStore = new AuthzStore();
